<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-gray-900 via-teal-900 to-emerald-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-teal-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-emerald-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-teal-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-teal-300">
            <i class="bx bx-chip mr-2"></i>Flexible Cloud Nodes
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          E1 Series Cloud <span class="text-teal-400">Instances</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl">
          Versatile cloud instances built for flexibility, cost-efficiency, and performance. Perfect balance of compute, memory, and networking resources for a wide range of applications.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-teal-600 to-emerald-700 hover:from-teal-700 hover:to-emerald-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-teal-500 text-teal-300 hover:bg-teal-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-chat mr-2"></i>
            Talk to Sales
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          <div class="bg-teal-500/10 backdrop-blur-sm rounded-xl p-4 border border-teal-500/30">
            <div class="text-3xl font-bold text-teal-400 mb-1">Flexible</div>
            <div class="text-sm text-gray-400">Configuration</div>
          </div>
          <div class="bg-teal-500/10 backdrop-blur-sm rounded-xl p-4 border border-teal-500/30">
            <div class="text-3xl font-bold text-teal-400 mb-1">Cost</div>
            <div class="text-sm text-gray-400">Efficient</div>
          </div>
          <div class="bg-teal-500/10 backdrop-blur-sm rounded-xl p-4 border border-teal-500/30">
            <div class="text-3xl font-bold text-teal-400 mb-1">Global</div>
            <div class="text-sm text-gray-400">Availability</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/e1-series-hero.png" alt="E1 Series Cloud Instances" class="w-full h-auto">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-teal-600 to-emerald-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-slider text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Flexible</div>
              <div class="text-xs">Scaling</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-wallet text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Best</div>
              <div class="text-xs">Value</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Features Section -->
<section class="py-20 bg-slate-950 border-y border-slate-800">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-teal-500/10 border border-teal-500/40 text-teal-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        E1 Series Features
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Designed for Flexibility and <span class="text-teal-400">Optimal Performance</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Versatile cloud instances built to handle diverse workloads with exceptional performance and value.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Flexible Configuration -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-teal-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-slider text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Flexible Configuration</h3>
        <p class="text-slate-300 mb-4">
          Easily scale compute, memory, and storage resources to match your workload requirements.
        </p>
        <div class="flex items-center text-teal-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">On-demand scaling</span>
        </div>
      </div>

      <!-- Cost-Efficiency -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-emerald-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-money text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Cost-Efficiency</h3>
        <p class="text-slate-300 mb-4">
          Optimized pricing structure that delivers maximum value without compromising performance.
        </p>
        <div class="flex items-center text-teal-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Best price-performance ratio</span>
        </div>
      </div>

      <!-- High Performance -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-cyan-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-tachometer text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">High Performance</h3>
        <p class="text-slate-300 mb-4">
          Powered by latest-generation processors and high-speed networking for exceptional performance.
        </p>
        <div class="flex items-center text-teal-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Latest-gen hardware</span>
        </div>
      </div>

      <!-- Enhanced Security -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-blue-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Enhanced Security</h3>
        <p class="text-slate-300 mb-4">
          Built-in security features including encryption, firewalls, and intrusion detection.
        </p>
        <div class="flex items-center text-teal-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Multi-layer security</span>
        </div>
      </div>

      <!-- Global Availability -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-indigo-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-cloud-download text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Global Availability</h3>
        <p class="text-slate-300 mb-4">
          Deploy your instances across multiple regions for low latency and high availability.
        </p>
        <div class="flex items-center text-teal-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">Multi-region deployment</span>
        </div>
      </div>

      <!-- Easy Management -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-purple-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-cog text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Easy Management</h3>
        <p class="text-slate-300 mb-4">
          Intuitive dashboard and API for effortless instance management and monitoring.
        </p>
        <div class="flex items-center text-teal-200 font-semibold">
          <i class="bx bx-check-circle mr-2"></i>
          <span class="text-sm">User-friendly interface</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Pricing Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        E1 Series Configurations
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Flexible <span class="text-emerald-400">Configuration Options</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Choose the perfect configuration to match your workload requirements.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
      <!-- E1.Small -->
      <div class="bg-slate-900/80 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-teal-500/40 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
        <div class="text-center mb-6">
          <div class="inline-block bg-gradient-to-r from-teal-600 to-emerald-700 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
            E1.Small
          </div>
          <h3 class="text-xl font-bold text-white mb-2">Starter</h3>
          <p class="text-sm text-slate-300">Ideal for small applications and development</p>
        </div>
        <ul class="space-y-3 mb-8">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-teal-600 text-xl mr-3"></i>
            <span class="font-medium">1 vCPU</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-teal-600 text-xl mr-3"></i>
            <span class="font-medium">2 GB RAM</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-teal-600 text-xl mr-3"></i>
            <span class="font-medium">20 GB SSD Storage</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-teal-600 text-xl mr-3"></i>
            <span class="font-medium">1 Gbps Network</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-teal-600 text-xl mr-3"></i>
            <span class="font-medium">1 TB Transfer</span>
          </li>
        </ul>
        <a routerLink="/contact" class="block w-full bg-gradient-to-r from-teal-600 to-emerald-700 hover:from-teal-700 hover:to-emerald-800 text-white font-semibold py-3 px-6 rounded-lg text-center transition-all duration-300">
          Contact Us
        </a>
      </div>

      <!-- E1.Medium -->
      <div class="bg-slate-900/80 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-emerald-500/40 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
        <div class="text-center mb-6">
          <div class="inline-block bg-gradient-to-r from-emerald-600 to-green-700 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
            E1.Medium
          </div>
          <h3 class="text-xl font-bold text-white mb-2">Standard</h3>
          <p class="text-sm text-slate-300">Perfect for medium-sized applications</p>
        </div>
        <ul class="space-y-3 mb-8">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-emerald-600 text-xl mr-3"></i>
            <span class="font-medium">2 vCPU</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-emerald-600 text-xl mr-3"></i>
            <span class="font-medium">4 GB RAM</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-emerald-600 text-xl mr-3"></i>
            <span class="font-medium">50 GB SSD Storage</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-emerald-600 text-xl mr-3"></i>
            <span class="font-medium">2 Gbps Network</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-emerald-600 text-xl mr-3"></i>
            <span class="font-medium">2 TB Transfer</span>
          </li>
        </ul>
        <a routerLink="/contact" class="block w-full bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800 text-white font-semibold py-3 px-6 rounded-lg text-center transition-all duration-300">
          Contact Us
        </a>
      </div>

      <!-- E1.Large -->
      <div class="bg-slate-900/80 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-cyan-500/40 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 relative">
        <div class="absolute -top-3 right-4">
          <span class="bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
            Popular
          </span>
        </div>
        <div class="text-center mb-6">
          <div class="inline-block bg-gradient-to-r from-cyan-600 to-teal-700 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
            E1.Large
          </div>
          <h3 class="text-xl font-bold text-white mb-2">Professional</h3>
          <p class="text-sm text-slate-300">Great for large applications and databases</p>
        </div>
        <ul class="space-y-3 mb-8">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-cyan-600 text-xl mr-3"></i>
            <span class="font-medium">4 vCPU</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-cyan-600 text-xl mr-3"></i>
            <span class="font-medium">8 GB RAM</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-cyan-600 text-xl mr-3"></i>
            <span class="font-medium">100 GB SSD Storage</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-cyan-600 text-xl mr-3"></i>
            <span class="font-medium">5 Gbps Network</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-cyan-600 text-xl mr-3"></i>
            <span class="font-medium">5 TB Transfer</span>
          </li>
        </ul>
        <a routerLink="/contact" class="block w-full bg-gradient-to-r from-cyan-600 to-teal-700 hover:from-cyan-700 hover:to-teal-800 text-white font-semibold py-3 px-6 rounded-lg text-center transition-all duration-300 shadow-lg">
          Contact Us
        </a>
      </div>

      <!-- E1.XLarge -->
      <div class="bg-slate-900/80 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-blue-500/40 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
        <div class="text-center mb-6">
          <div class="inline-block bg-gradient-to-r from-blue-600 to-cyan-700 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
            E1.XLarge
          </div>
          <h3 class="text-xl font-bold text-white mb-2">Enterprise</h3>
          <p class="text-sm text-slate-300">For high-performance demanding applications</p>
        </div>
        <ul class="space-y-3 mb-8">
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 text-xl mr-3"></i>
            <span class="font-medium">8 vCPU</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 text-xl mr-3"></i>
            <span class="font-medium">16 GB RAM</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 text-xl mr-3"></i>
            <span class="font-medium">200 GB SSD Storage</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 text-xl mr-3"></i>
            <span class="font-medium">10 Gbps Network</span>
          </li>
          <li class="flex items-center text-slate-200">
            <i class="bx bx-check-circle text-blue-600 text-xl mr-3"></i>
            <span class="font-medium">10 TB Transfer</span>
          </li>
        </ul>
        <a routerLink="/contact" class="block w-full bg-gradient-to-r from-blue-600 to-cyan-700 hover:from-blue-700 hover:to-cyan-800 text-white font-semibold py-3 px-6 rounded-lg text-center transition-all duration-300">
          Contact Us
        </a>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-teal-600 via-emerald-700 to-green-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-teal-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-emerald-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
      <div class="lg:col-span-2">
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
          Ready to Get Started with E1 Series?
        </h2>
        <p class="text-xl text-gray-100 mb-8">
          Contact us to deploy your flexible, cost-efficient cloud instances with optimal performance for diverse workloads.
        </p>
        
        <div class="flex flex-wrap gap-6">
          <div class="flex items-center">
            <i class="bx bx-slider text-3xl mr-3"></i>
            <span class="text-lg">Flexible scaling</span>
          </div>
          <div class="flex items-center">
            <i class="bx bx-money text-3xl mr-3"></i>
            <span class="text-lg">Best value pricing</span>
          </div>
          <div class="flex items-center">
            <i class="bx bx-support text-3xl mr-3"></i>
            <span class="text-lg">24/7 support</span>
          </div>
        </div>
      </div>
      
      <div class="flex flex-col gap-4">
        <a routerLink="/contact" class="bg-white text-teal-600 font-bold py-4 px-8 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl">
          <i class="bx bx-rocket mr-2 text-xl"></i>
          <span class="text-lg">Contact Us</span>
        </a>
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <div class="text-2xl font-bold mb-1">4</div>
            <div class="text-sm text-gray-200">Configurations</div>
          </div>
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <div class="text-2xl font-bold mb-1">10Gbps</div>
            <div class="text-sm text-gray-200">Max Network</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
